import classNames from "classnames"

import CategoryBadge from "./CategoryBadge"
import { DEFAULT_ARTICLE_SRCSET_WIDTHS } from "./imageConfig"

import Link from "@components/elements/Link"
import GatsbyStoryblokImage from "@components/elements/V2/GatsbyStoryblokImage"
import Typography from "@components/elements/V2/Typography"
import LayoutItem from "@components/V2/Layouts/LayoutItem"
import { Color } from "@constants/V2/color"
import { getTransparentBorderColorClass } from "@utils/V2/color"
import React, { ElementType } from "react"

type DefaultArticleCardProps = {
  title: string
  imageUrl: string
  imageAlt: string
  category?: string
  readTimeText?: string
  articleLink?: YocoCom.YocoLink
  textColor: Color
}

const DefaultArticleCard = ({
  title,
  imageUrl,
  imageAlt,
  category,
  readTimeText,
  articleLink,
  textColor,
}: DefaultArticleCardProps) => {
  const Wrapper = (articleLink ? Link : "div") as ElementType

  return (
    <LayoutItem
      as={Wrapper}
      animateOnScroll
      className={classNames(
        "group col-span-full flex gap-16 border-b pb-16 sm:col-span-2 md:col-span-3 md:flex-col md:gap-0 md:rounded-20 md:border-b-0 md:p-10 md:ring-1 md:ring-inset md:ring-transparent md:transition-shadow md:duration-200 md:ease-out md:hover:ring-charcoal/10 md:focus-visible:ring-charcoal/10 lg:col-span-4",
        getTransparentBorderColorClass(textColor),
        { "cursor-pointer": !!articleLink }
      )}
      {...(articleLink ? { link: articleLink } : {})}
    >
      <div className="relative aspect-square w-[100px] flex-none self-start overflow-hidden rounded-16 md:aspect-[4/3] md:w-full md:self-auto md:rounded-10">
        <div className="absolute inset-0 md:transition-transform md:duration-200 md:ease-out md:group-hover:scale-105 md:group-focus-visible:scale-105">
          <GatsbyStoryblokImage
            image={imageUrl}
            alt={imageAlt}
            aspectRatio="fullWidth"
            imageObjectFit="cover"
            sizes="(min-width: 1200px) 26vw, (min-width: 900px) 42vw, 100px"
            srcSetWidths={DEFAULT_ARTICLE_SRCSET_WIDTHS}
            preserveAnimation
            className="h-full w-full"
          />
        </div>

        {category && (
          <CategoryBadge
            category={category}
            className="absolute bottom-10 left-10 hidden md:block"
          />
        )}
      </div>

      <div className="flex flex-col justify-center gap-10 md:justify-start md:px-10 md:py-20">
        <Typography
          className="md:transition-colors md:duration-200 md:ease-out md:group-hover:text-blue md:group-focus-visible:text-blue"
          element="h2"
          size="body-lg-dual"
          font="grotesk"
          weight="medium"
          color={textColor}
          text={title}
        />

        {(category || readTimeText) && (
          <div className="flex items-center gap-8">
            {category && (
              <CategoryBadge category={category} className="md:hidden" />
            )}

            {readTimeText && (
              <Typography
                className="opacity-60"
                size="body-sm"
                font="grotesk"
                weight="book"
                color={textColor}
                text={readTimeText}
              />
            )}
          </div>
        )}
      </div>
    </LayoutItem>
  )
}

export default DefaultArticleCard
